import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { FaGraduationCap, FaBriefcase, FaCode, FaTrophy } from 'react-icons/fa';

const About = () => {
    const { isDark } = useTheme();

    const experiences = [
        {
            company: "Technuf",
            role: "Software Engineer",
            period: "September 2025 - Present",
            description: "Developing Proposal Connect, an AI-powered web application using Nuxt.js, multi-agentic AI, and Supabase.",
            tags: ["Nuxt.js", "Vue.js", "AI", "Supabase"]
        },
        {
            company: "North South University",
            role: "Research Assistant (Part-time)",
            period: "July 2025 - Present",
            description: "Contributing to research on autonomous indoor delivery robots, combining innovation with hands-on work in robotics.",
            tags: ["Robotics", "AI", "Research"]
        },
        {
            company: "Edvive",
            role: "Full Stack Web Developer",
            period: "November 2020 - March 2021",
            description: "Developed the first prototype for a peer-to-peer English learning platform, securing investor interest.",
            tags: ["Full Stack", "React", "Node.js"]
        }
    ];

    const skills = {
        languages: ["Python", "JavaScript", "C/C++"],
        frameworks: ["React", "Vue", "NodeJS", "NextJS", "NuxtJS", "Django", "TailwindCSS"],
        databases: ["MySQL", "MongoDB", "DynamoDB", "Firestore"],
        cloud: ["Firebase", "AWS"],
        others: ["PyTorch", "REST API", "FastAPI", "Git"]
    };

    const achievements = [
        { title: "National Snooker Championship 2025", position: "Top 16" },
        { title: "Inter NSU Programming Competition 2023", position: "5th Position" },
        { title: "NSU ACM Student Chapter", position: "Senior Member (2022-2024)" }
    ];

    return (
        <div className='flex-1 py-12'>
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-12"
            >
                <h1 className={`font-mono font-bold text-3xl md:text-4xl lg:text-5xl mb-4 ${
                    isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                }`}>
                    <span className={isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'}>
                        $
                    </span>
                    {' '}cat ./about_me.md
                </h1>
                <p className={`font-mono text-base md:text-lg max-w-3xl ${
                    isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
                }`}>
                    <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
                        //
                    </span>
                    {' '}Computer Science graduate passionate about building scalable web applications and solving complex problems
                </p>
            </motion.div>

            {/* Education Section */}
            <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mb-12"
            >
                <div className={`flex items-center gap-3 mb-6 ${
                    isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                }`}>
                    <FaGraduationCap className={`text-2xl ${
                        isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'
                    }`} />
                    <h2 className="font-mono font-bold text-2xl md:text-3xl">
                        <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
                            &gt;
                        </span>
                        {' '}Education
                    </h2>
                </div>

                <div className={`rounded-lg border p-6 ${
                    isDark
                        ? 'bg-dark-bg-card border-dark-border'
                        : 'bg-light-bg-card border-light-border shadow-sm'
                }`}>
                    <h3 className={`font-mono font-semibold text-xl mb-2 ${
                        isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                    }`}>
                        B.Sc. in Computer Science and Engineering
                    </h3>
                    <p className={`font-mono text-sm mb-1 ${
                        isDark ? 'text-dark-brand-secondary' : 'text-light-brand-secondary'
                    }`}>
                        North South University, Dhaka
                    </p>
                    <p className={`font-mono text-sm mb-2 ${
                        isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
                    }`}>
                        October 2021 - April 2025 | CGPA: 3.48
                    </p>
                </div>
            </motion.section>

            {/* Experience Section */}
            <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mb-12"
            >
                <div className={`flex items-center gap-3 mb-6 ${
                    isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                }`}>
                    <FaBriefcase className={`text-2xl ${
                        isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'
                    }`} />
                    <h2 className="font-mono font-bold text-2xl md:text-3xl">
                        <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
                            &gt;
                        </span>
                        {' '}Experience
                    </h2>
                </div>

                <div className="space-y-6">
                    {experiences.map((exp, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className={`rounded-lg border p-6 ${
                                isDark
                                    ? 'bg-dark-bg-card border-dark-border hover:border-dark-brand-primary'
                                    : 'bg-light-bg-card border-light-border hover:border-light-brand-primary shadow-sm'
                            } transition-all`}
                        >
                            {/* Terminal-style header */}
                            <div className={`px-3 py-2 -mx-6 -mt-6 mb-4 border-b font-mono text-xs flex items-center gap-2 ${
                                isDark
                                    ? 'bg-dark-bg-tertiary border-dark-border'
                                    : 'bg-light-bg-tertiary border-light-border'
                            }`}>
                                <div className="flex gap-1.5">
                                    <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                                </div>
                                <span className={isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}>
                                    {exp.company}.sh
                                </span>
                            </div>

                            <h3 className={`font-mono font-semibold text-lg mb-1 ${
                                isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                            }`}>
                                {exp.role}
                            </h3>
                            <p className={`font-mono text-sm mb-1 ${
                                isDark ? 'text-dark-brand-secondary' : 'text-light-brand-secondary'
                            }`}>
                                @ {exp.company}
                            </p>
                            <p className={`font-mono text-xs mb-3 ${
                                isDark ? 'text-dark-text-muted' : 'text-light-text-muted'
                            }`}>
                                {exp.period}
                            </p>
                            <p className={`font-mono text-sm mb-4 ${
                                isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
                            }`}>
                                {exp.description}
                            </p>

                            {/* Tags */}
                            <div className="flex flex-wrap gap-2">
                                {exp.tags.map((tag, idx) => (
                                    <span
                                        key={idx}
                                        className={`font-mono text-xs px-2 py-1 rounded border ${
                                            isDark
                                                ? 'text-dark-brand-secondary border-dark-border bg-dark-bg-tertiary'
                                                : 'text-light-brand-secondary border-light-border bg-light-bg-tertiary'
                                        }`}
                                    >
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.section>

            {/* Skills Section */}
            <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mb-12"
            >
                <div className={`flex items-center gap-3 mb-6 ${
                    isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                }`}>
                    <FaCode className={`text-2xl ${
                        isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'
                    }`} />
                    <h2 className="font-mono font-bold text-2xl md:text-3xl">
                        <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
                            &gt;
                        </span>
                        {' '}Technical Skills
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(skills).map(([category, items], index) => (
                        <motion.div
                            key={category}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className={`rounded-lg border p-4 ${
                                isDark
                                    ? 'bg-dark-bg-card border-dark-border'
                                    : 'bg-light-bg-card border-light-border shadow-sm'
                            }`}
                        >
                            <h3 className={`font-mono font-semibold text-sm mb-3 ${
                                isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'
                            }`}>
                                $ ls ./{category}/
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {items.map((skill, idx) => (
                                    <span
                                        key={idx}
                                        className={`font-mono text-xs px-2 py-1 rounded ${
                                            isDark
                                                ? 'text-dark-text-primary bg-dark-bg-tertiary'
                                                : 'text-light-text-primary bg-light-bg-tertiary'
                                        }`}
                                    >
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.section>

            {/* Achievements Section */}
            <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
            >
                <div className={`flex items-center gap-3 mb-6 ${
                    isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                }`}>
                    <FaTrophy className={`text-2xl ${
                        isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'
                    }`} />
                    <h2 className="font-mono font-bold text-2xl md:text-3xl">
                        <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
                            &gt;
                        </span>
                        {' '}Achievements
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {achievements.map((achievement, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -5 }}
                            className={`rounded-lg border p-4 text-center ${
                                isDark
                                    ? 'bg-dark-bg-card border-dark-border hover:border-dark-brand-primary'
                                    : 'bg-light-bg-card border-light-border hover:border-light-brand-primary shadow-sm'
                            } transition-all`}
                        >
                            <p className={`font-mono text-sm mb-2 ${
                                isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                            }`}>
                                {achievement.title}
                            </p>
                            <p className={`font-mono font-semibold text-lg ${
                                isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'
                            }`}>
                                {achievement.position}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </motion.section>
        </div>
    );
};

export default About;
